/**
 * Certifications page content (/Certifications).
 *
 * Every string in this file is transcribed verbatim from the approved
 * "Certification Process Page (SEO Optimized 2026)" requirement document
 * (MBA_MERN_Solutions/Certification Process Page.docx). Do not reword, shorten
 * or extend this copy — update the document first.
 *
 * ON THE URL: the document suggests the slug /certification-process. This page
 * has always lived at /Certifications and that route is linked from the header
 * and elsewhere, so it stays — adding a second route for the same content would
 * be a duplicate URL, which the same document's SEO section argues against.
 *
 * NOTE `listLabel` is per step on purpose: the document introduces each list
 * with different wording ("During consultation, our experts analyze:",
 * "We review:", "Documentation may include:" ...). Those are not
 * interchangeable and must not be collapsed into one shared label.
 *
 * `image` points at the illustrations already in
 * public/assets/certification-process — the same six the homepage process
 * section uses. No new artwork was introduced for this page.
 */

export const certificationSeo = {
  title:
    "Certification Process | ISO Consultancy & Implementation Support | MBA Solutions",
  description:
    "Understand MBA Solutions certification process including consultation, gap assessment, documentation, implementation, internal audit and certification audit support for ISO and compliance standards.",
  keywords: [
    "ISO Certification Process",
    "Certification Consultancy Process",
    "ISO Implementation Support",
    "ISO Consultant",
    "Compliance Certification Services",
    "ISO Audit Preparation",
    "Management System Implementation",
    "Certification Support Services",
    "ISO Documentation Support",
    "Business Compliance Consultancy",
  ].join(", "),
};

export const heroContent = {
  h1: "Our Certification Process",
  subtitle: "Turning Standards Into Successful Business Systems",
  paragraphs: [
    "Achieving certification requires more than preparing documents — it requires proper planning, implementation and continuous improvement.",
    "MBA Solutions follows a structured and practical certification process to help organizations successfully implement management systems and prepare for certification audits.",
    "Our experts guide businesses through every stage of the journey, ensuring that systems are developed according to international standards and business requirements.",
    "We support organizations with ISO certifications, food safety standards, sustainability certifications, textile compliance and other management system requirements.",
  ],
};

export const outcomeLabel = "Outcome:";

export const stepsContent = {
  title: "Our Six-Step Certification Journey",
  subtitle: "A Structured Journey From Assessment To Successful Certification",
  steps: [
    {
      number: "1",
      image: "/assets/certification-process/consultation.webp",
      title: "Step 1: Consultation & Planning",
      subtitle: "Understanding Your Business Goals & Certification Requirements",
      description:
        "The first step is understanding your organization, operations, industry requirements and certification objectives.",
      listLabel: "During consultation, our experts analyze:",
      items: [
        "Business processes",
        "Required certification standard",
        "Industry requirements",
        "Current challenges",
        "Certification timeline",
      ],
      outcomes: [
        "Clear certification roadmap",
        "Understanding of requirements",
        "Implementation strategy",
      ],
    },
    {
      number: "2",
      image: "/assets/certification-process/gap-assessment.webp",
      title: "Step 2: Gap Assessment",
      subtitle: "Identifying Current System Gaps",
      description:
        "Before implementation, we evaluate your existing processes against the requirements of the selected standard. Our gap assessment helps identify areas that require improvement.",
      listLabel: "We review:",
      items: [
        "Existing procedures",
        "Current practices",
        "Compliance level",
        "Risks and improvement opportunities",
      ],
      outcomes: [
        "Gap assessment report",
        "Improvement action plan",
        "Clear implementation direction",
      ],
    },
    {
      number: "3",
      image: "/assets/certification-process/documentation.webp",
      title: "Step 3: Documentation Development",
      subtitle: "Creating A Strong Management System Foundation",
      description:
        "Documentation is an important part of any certification process. MBA Solutions helps organizations prepare required documents according to applicable standards.",
      listLabel: "Documentation may include:",
      items: [
        "Policies",
        "Procedures",
        "Manuals",
        "Forms",
        "Records",
        "Risk assessments",
        "Process documents",
      ],
      outcomes: [
        "Complete management system documentation",
        "Certification-ready structure",
      ],
    },
    {
      number: "4",
      image: "/assets/certification-process/implementation.webp",
      title: "Step 4: Implementation Support",
      subtitle: "Applying The System Into Daily Operations",
      description:
        "A successful certification depends on effective implementation. We support your team in applying the developed systems within your daily business operations.",
      listLabel: "Our support includes:",
      items: [
        "Employee awareness",
        "Process implementation",
        "Record management",
        "Compliance improvement",
        "Practical guidance",
      ],
      outcomes: [
        "Working management system",
        "Employee understanding",
        "Operational compliance",
      ],
    },
    {
      number: "5",
      image: "/assets/certification-process/internal-audit.webp",
      title: "Step 5: Internal Audit",
      subtitle: "Checking System Effectiveness Before Certification",
      description:
        "Before the final certification audit, we conduct internal reviews to evaluate system performance.",
      listLabel: "Our internal audit process helps identify:",
      items: [
        "Non-conformities",
        "Process weaknesses",
        "Improvement areas",
        "Compliance gaps",
      ],
      /* the document adds this sentence after the list on this step only */
      note: "We guide organizations in implementing corrective actions.",
      outcomes: [
        "Improved audit readiness",
        "Reduced certification risks",
        "Stronger management system",
      ],
    },
    {
      number: "6",
      image: "/assets/certification-process/certification-support.webp",
      title: "Step 6: Certification Audit Support",
      subtitle: "Supporting You During The Final Certification Stage",
      description:
        "MBA Solutions provides guidance throughout the certification audit process.",
      listLabel: "We help organizations prepare for assessment by:",
      items: [
        "Reviewing final readiness",
        "Supporting audit coordination",
        "Guiding corrective actions",
        "Helping resolve audit findings",
      ],
      outcomes: [
        "Smooth certification process",
        "Better audit confidence",
        "Successful certification preparation",
      ],
    },
  ],
};

export const approachContent = {
  title: "Why Our Certification Approach Works",
  subtitle: "Practical. Professional. Business-Focused.",
  intro:
    "MBA Solutions focuses on creating systems that deliver real business improvement.",
  listLabel: "Our approach provides:",
  items: [
    "Industry-specific solutions",
    "Experienced consultancy support",
    "Practical implementation methods",
    "Complete certification guidance",
    "Long-term improvement focus",
  ],
};

export const industriesContent = {
  title: "Industries We Support",
  intro: "MBA Solutions supports organizations across:",
  groups: [
    {
      icon: "food",
      title: "Food Industry",
      to: "/industries-we-serve#food-beverage",
      items: ["HACCP", "ISO 22000", "FSSC 22000", "Food Safety Systems"],
    },
    {
      icon: "textile",
      title: "Textile Industry",
      to: "/industries-we-serve#textile-garments",
      items: ["GOTS", "GRS", "WRAP", "Sedex / SMETA"],
    },
    {
      icon: "manufacturing",
      title: "Manufacturing",
      to: "/industries-we-serve#manufacturing",
      items: ["ISO 9001", "ISO 14001", "ISO 45001"],
    },
    {
      icon: "it",
      title: "IT Industry",
      to: "/industries-we-serve#it-software",
      items: ["ISO 27001", "Quality Management Systems"],
    },
    {
      icon: "export",
      title: "Export Businesses",
      to: "/industries-we-serve#export-businesses",
      items: ["Compliance Certifications", "Sustainability Requirements"],
    },
    {
      icon: "supplyChain",
      title: "Supply Chain Businesses",
      to: "/industries-we-serve#supply-chain-logistics",
      items: ["FSC CoC", "PEFC", "Responsible Sourcing"],
    },
  ],
};

export const faqContent = {
  title: "Frequently Asked Questions",
  items: [
    {
      question: "Q1: How long does the certification process take?",
      answer:
        "The timeline depends on organization size, current system maturity and certification requirements.",
    },
    {
      question:
        "Q2: Does MBA Solutions provide complete implementation support?",
      answer:
        "Yes, we support organizations from consultation and gap assessment to audit preparation.",
    },
    {
      question:
        "Q3: Can you help businesses that have no existing management system?",
      answer:
        "Yes, we help organizations build complete management systems from the beginning.",
    },
    {
      question: "Q4: Which certifications do you support?",
      answer:
        "We support ISO certifications, food safety standards, textile compliance, sustainability frameworks and supply chain certifications.",
    },
  ],
};

export const finalCtaContent = {
  title: "Ready To Start Your Certification Journey?",
  paragraph:
    "Build a stronger, compliant and globally recognized business with MBA Solutions.",
  /* Both buttons are the document's own. They are given DIFFERENT targets so the
     pair is not two links to one place: the primary goes to the contact page,
     the secondary to the enquiry form already at the foot of this page. */
  cta: { label: "Get Certification Consultation", to: "/contact" },
  secondaryCta: {
    label: "Contact MBA Solutions Today",
    href: "#certification-enquiry",
  },
};
